/**
 * SectionManager - Section visibility and highlighting
 *
 * Uses inline styles to match the current runtime's approach:
 *   Toggle: element.style.display = '' / 'none'
 *   Highlight: inline outline style, cleared on null sectionId (no timeout)
 */

export class SectionManager {
  private highlightedEl: HTMLElement | null = null;

  /**
   * Toggle section visibility via inline display style.
   */
  toggleSection(sectionId: string, enabled: boolean): void {
    const el = this.getSectionElement(sectionId);
    if (!el) return;
    el.style.display = enabled ? '' : 'none';
  }

  /**
   * Toggle every desktop/mobile navbar item that points at a section.
   */
  toggleNavSection(sectionId: string, visible: boolean): void {
    document.querySelectorAll<HTMLElement>(`[data-nav-section="${sectionId}"]`).forEach((el) => {
      el.style.display = visible ? '' : 'none';
    });
  }

  /**
   * Highlight a section with an outline.
   * Pass null to clear the current highlight.
   */
  highlightSection(sectionId: string | null): void {
    // Clear previous highlight
    if (this.highlightedEl) {
      this.highlightedEl.style.outline = '';
      this.highlightedEl.style.outlineOffset = '';
      this.highlightedEl = null;
    }

    if (!sectionId) return;

    const el = this.getSectionElement(sectionId);
    if (!el) return;

    el.style.outline = '2px solid rgba(59, 130, 246, 0.5)';
    el.style.outlineOffset = '-2px';
    this.highlightedEl = el;
  }

  /**
   * Smooth scroll to a section.
   */
  scrollToSection(sectionId: string): void {
    const el = this.getSectionElement(sectionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Get a section element by its data-dr-section attribute.
   */
  getSectionElement(sectionId: string): HTMLElement | null {
    return document.querySelector(`[data-dr-section="${sectionId}"]`);
  }

  /**
   * Get all section elements.
   */
  getAllSections(): HTMLElement[] {
    return Array.from(document.querySelectorAll('[data-dr-section]'));
  }

  destroy(): void {
    this.highlightSection(null);
  }
}
